import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import ManualLayout from '../components/ManualLayout';
import { useManualArticle } from '../hooks/useManualArticle';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { RiskMetricsCalculator } from '../components/RiskMetricsCalculator';

const CALCULATOR_SLUGS = [
  'reports/reports-risk',
  'reports/balance-drawdown-over-20',
];

/** Remove leading H1 title from markdown to avoid duplication with the page header */
function stripLeadingTitle(md: string): string {
  // Remove leading "# Title\n" and any immediately following blank description paragraph
  const lines = md.split('\n');
  let start = 0;
  // Skip leading blank lines
  while (start < lines.length && lines[start].trim() === '') start++;
  // If first non-blank line is an H1, skip it
  if (start < lines.length && /^#\s+/.test(lines[start])) {
    start++;
    // Skip blank lines after title
    while (start < lines.length && lines[start].trim() === '') start++;
  }
  return lines.slice(start).join('\n');
}

interface ManualArticlePageProps {
  slug: string;
  language: 'ja' | 'en';
  onLanguageChange: (lang: 'ja' | 'en') => void;
}

export default function ManualArticlePage({ slug, language, onLanguageChange }: ManualArticlePageProps) {
  const { article, loading, error, refetch } = useManualArticle(slug, language);

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children, ...props }: any) => {
        if (href === '/contact.html') {
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                document.dispatchEvent(new Event('open-contact-modal'));
              }}
              {...props}
            >
              {children}
            </a>
          );
        }
        if (href?.startsWith('#/manual/') || href?.startsWith('/manual/')) {
          // Ensure /manual/article/ format for article links
          let resolved = href.startsWith('#') ? href.slice(1) : href;
          if (resolved.startsWith('/manual/article/')) {
            // Already correct format
          } else if (resolved.match(/^\/manual\/[a-z0-9-]+\/[a-z0-9-]+$/)) {
            // Old format /manual/section/slug -> /manual/article/section/slug
            resolved = resolved.replace(/^\/manual\//, '/manual/article/');
          }
          const path = `#${resolved}`;
          return (
            <a
              href={path}
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = path;
              }}
              {...props}
            >
              {children}
            </a>
          );
        }
        if (href?.startsWith('#')) {
          return (
            <a href={href} {...props}>
              {children}
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
    }),
    []
  );

  if (loading) {
    return (
      <ManualLayout language={language} onLanguageChange={onLanguageChange}>
        <LoadingState />
      </ManualLayout>
    );
  }

  if (error) {
    if (error instanceof Error) {
      return <ErrorDisplay error={error} />;
    }
    return (
      <ManualLayout language={language} onLanguageChange={onLanguageChange}>
        <ErrorState
          message={typeof error === 'string' ? error : (language === 'en' ? 'An error occurred' : 'エラーが発生しました')}
          onRetry={refetch}
          onGoBack={() => window.location.hash = '#/manual'}
        />
      </ManualLayout>
    );
  }

  if (!article) {
    return (
      <ManualLayout language={language} onLanguageChange={onLanguageChange}>
        <ErrorState
          message={language === 'en' ? 'The specified page does not exist.' : '指定されたページは存在しません。'}
          onGoBack={() => window.location.hash = '#/manual'}
          backLabel={language === 'en' ? 'Back to Manual' : 'マニュアルTOPへ戻る'}
        />
      </ManualLayout>
    );
  }

  return (
    <ManualLayout currentPath={`/manual/article/${slug}`} language={language} onLanguageChange={onLanguageChange}>
      <article className="manual-article">
        <div className="manual-article-header">
          <h1 className="manual-article-title">{article.title}</h1>
        </div>

        <div className="manual-article-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={markdownComponents}
          >
            {stripLeadingTitle(article.content_md)}
          </ReactMarkdown>
        </div>

        {CALCULATOR_SLUGS.some(s => slug?.endsWith(s)) && (
          <div className="manual-article-calculator">
            <RiskMetricsCalculator />
          </div>
        )}
      </article>
    </ManualLayout>
  );
}
